// src/Pages/BusManagement.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api/api.js';
import BusForm from '../Components/BusForm.js';
import BusList from '../Components/BusList.js';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import styles from './BusManagement.module.css';

// Estado inicial del formulario actualizado con bus_type_id y driver_id
const INITIAL_FORM_STATE = {
  id: '',
  plate: '',
  bus_type_id: '',
  driver_id: '',
  status: 'available'
};

const BusManagement = () => {
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]); // Cambiado de employees a drivers
  const [busTypes, setBusTypes] = useState([]); // Nuevo estado para tipos de bus
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // --- Funciones de Fetching (Memorizadas) ---

  // Cargar conductores (usuarios con rol 'employee' y position 'conductor')
  const fetchDrivers = useCallback(async () => {
    try {
      const res = await api.get('/user');
      const allUsers = res.data.users || [];

      // Filtrar solo empleados con position 'conductor'
      const conductores = allUsers.filter(u =>
        (u.Rol || u.role) === 'employee' &&
        (u.Position || u.position) === 'conductor'
      );


      setDrivers(conductores);
    } catch (err) {
      showToast('Error al cargar conductores', { type: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar tipos de bus
  const fetchBusTypes = useCallback(async () => {
    try {
      const res = await api.get('/bus_type');

      setBusTypes(res.data || []);
    } catch (err) {
      showToast('Error al cargar tipos de bus', { type: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBuses = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/bus');

      setBuses(res.data || []);
    } catch (err) {
      showToast('Error al cargar buses', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBuses();
    fetchDrivers();
    fetchBusTypes();
  }, [fetchBuses, fetchDrivers, fetchBusTypes]);

  // --- Lógica de Derivación y Optimización (useMemo) ---

  // Opciones de conductores con nombre completo
  const driverOptions = useMemo(() =>
    drivers.map(driver => ({
      value: driver.ID || driver.id,
      label: `${driver.Name || driver.name} ${driver.Last_name || driver.last_name}`
    }))
  , [drivers]);

  // Opciones de tipos de bus
  const busTypeOptions = useMemo(() =>
    busTypes.map(type => ({
      value: type.ID || type.id,
      label: `${type.Name} (${type.Number_seats} asientos)`
    }))
  , [busTypes]);

  // --- Funciones de Mutación ---

  const handleUpdateBusList = (updatedBus) => {
    setBuses(prevBuses => {
      const exists = prevBuses.some(b => String(b.ID || b.id) === String(updatedBus.id));
      if (updatedBus.id && exists) {
        return prevBuses.map(bus =>
          String(bus.ID || bus.id) === String(updatedBus.id) ? { ...bus, ...updatedBus } : bus
        );
      }
      return [...prevBuses, updatedBus];
    });
  };

  const handleDeleteBus = useCallback(async (id) => {
    const ok = await confirm('¿Eliminar este bus? Esta acción no se puede deshacer.', {
      title: 'Confirmar eliminación',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/bus/${id}`);
      setBuses(prevBuses => prevBuses.filter(bus => String(bus.ID || bus.id) !== String(id)));
      if (String(id) === form.id) setForm(INITIAL_FORM_STATE);
      showToast('Bus eliminado correctamente.', { type: 'success' });
    } catch (err) {
      showToast('No se pudo eliminar el bus.', { type: 'error' });
    }
  }, [form.id, confirm, showToast]);

  const formRef = useRef(null);

  const handleEdit = useCallback((bus) => {
    setForm({
      id: String(bus.ID || bus.id),
      plate: bus.Plate || bus.plate,
      bus_type_id: String(bus.ID_type_bus || bus.bus_type_id || ''),
      driver_id: String(bus.ID_driver || bus.driver_id || ''),
      status: bus.Status || bus.status || 'available'
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  // --- Renderizado ---

  return (
    <div className={styles.container}>
      <h2>Gestión de Buses</h2>

      {isLoading && <div className={styles.loadingMessage}>Cargando datos...</div>}

      {!isLoading && (
        <>
          <div ref={formRef}>
            <BusForm
              form={form}
              setForm={setForm}
              resetForm={() => setForm(INITIAL_FORM_STATE)}
              driverOptions={driverOptions}
              busTypeOptions={busTypeOptions}
              onSuccess={handleUpdateBusList}
            />
          </div>

          <BusList
            buses={buses}
            driverOptions={driverOptions}
            busTypeOptions={busTypeOptions}
            onEdit={handleEdit}
            onDelete={handleDeleteBus}
          />
        </>
      )}
    </div>
  );
};

export default BusManagement;
